<template lang="">
  <div class="custom-size-selector">
    <HorizontalScroller>
      <div
        v-for="(size, index) in sizeOptions"
        :key="index"
        class="custom-size-selector__item"
        :class="{ 'custom-size-selector__item--unselected': size.quantity === 0 }"
      >
        <div class="custom-size-selector__item-title">{{ itemTitle(size) }}</div>
        <div class="custom-size-selector__item-subtitle">{{ itemSubtitle(size) }}</div>
        <QuantityInput
          is-custom-size
          :min-quantity="0"
          :quantity="size.quantity"
          :variant-id="variantId"
          @on-change="value => handleQuantityUpdate(size.index, value.value)"
        />
        <div
          class="custom-size-selector__item-price"
          :class="{
            'custom-size-selector__item-price--loading': isPricingLoading,
            'custom-size-selector__item-price--visible': size.quantity > 0
          }"
        >
          {{ getPrice(size) }}
        </div>
      </div>
    </HorizontalScroller>
  </div>
</template>

<script>
import { mapState } from "pinia";
import QuantityInput from "./QuantityInput.vue";
import HorizontalScroller from "../common/HorizontalScroller.vue";
import { n } from "../../vue-cart/helpers/currency";
import { MAX_PRINT_SIZES_GROUPED } from "../common/constants";
import { zipArray } from "../utils";
import { useDtfStore } from "../store";

export default {
  name: "CustomSizeSelector",
  components: {
    QuantityInput,
    HorizontalScroller
  },
  props: {
    sizes: {
      type: Array,
      default: () => []
    },
    preset: {
      type: String,
      default: "custom"
    },
    artworkId: {
      type: String,
      default: ""
    }
  },
  emits: ["on-change"],
  computed: {
    ...mapState(useDtfStore, ["isPricingLoading", "variantId", "getBogoAdjustedPrice"]),
    sizeOptions() {
      return zipArray(MAX_PRINT_SIZES_GROUPED[this.preset], this.sizes);
    }
  },
  methods: {
    itemTitle(size) {
      if (this.preset === "full-front") {
        if (size?.sizeRange?.toLowerCase?.() === "newborn") return "Newborn";
        if (size?.category?.toLowerCase?.() === "infant") return "Infant";
      }

      const range = size.sizeRange ? `(${size.sizeRange})` : "";
      return `${size.category} ${range}`;
    },
    itemSubtitle(size) {
      return `${size.width}" x ${size.height}"`;
    },
    getPrice(size) {
      const sizeKey = `${size.width}x${size.height}`;
      const bogoPrice = this.getBogoAdjustedPrice(this.artworkId, size.id, sizeKey);

      if (bogoPrice) {
        return n(bogoPrice.discountedTotalPrice, "currency");
      }

      return n(size.unitPrice * size.quantity, "currency");
    },
    handleQuantityUpdate(index, value) {
      this.$emit("on-change", { index, value });
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.custom-size-selector {
  &__item {
    height: 114px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    gap: 3px;
    padding: 8px;
    border-radius: 8px;
    box-shadow: 0px 0px 4px 0px rgba($black, 0.25);
    background-color: $white;
    margin-right: 10px;

    &--unselected {
      background-color: $crafterpay-gray-modern-100;
      box-shadow: none;
    }

    @include with-tablet {
      height: 122px;
    }

    @include with-desktop {
      height: 128px;
    }
  }

  &__item-title {
    font-size: 14px;
    font-weight: 700;
    line-height: 16.1px;
    text-align: center;
    white-space: nowrap;
    text-transform: capitalize;
  }

  &__item-subtitle {
    font-size: 13px;
    font-weight: 600;
    line-height: 15.73px;
    text-align: center;
    color: $crafterpay-gray-modern-600;
  }

  &__item-price {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.1px;
    text-align: center;
    opacity: 0;
    height: 20px;

    &--visible {
      opacity: 1;
    }

    &--loading {
      opacity: 0.25;
    }
  }
}
</style>
